import { ChakraProvider, Flex, Spinner, useTheme, useToast } from "@chakra-ui/react";
import { ChakraThemeProvider } from "@/components/Providers/ChakraThemeProvider";
import { usePageContext } from "@/utils/usePageContext";
import type { ComparePageData } from "@/types/ComparePageData";
import type { ComparePagePathProps } from "@/types/ComparePagePathProps";
import { getCompareComponents, useCompareManagerState } from "@/services/useCompareManager";
import { useMemo } from "react";
import { CompareSectionRow } from "@/components/CompareManager/CompareSectionRow/CompareSectionRow";
import BaseAccordion from "@/components/Accordion/Accordion/BaseAccordion";
import { COMPARE_ALIGNED_COMPONENTS, getCompareSectionForComponent } from "@/services/compareUtils";
import type { ShareClassTranslationMap } from "@/types/ShareClassTranslationMap";

export default function ComparePageEntryPoint() {
	const context = usePageContext<{ data: ComparePageData }>();
	const { location, channel, language } = context.query as ComparePagePathProps;

	const compareOptions = useMemo(() => {
		return {
			shouldFetchData: true
		};
	}, []);

	const {
		data,
		externalIds,
		componentList,
		isLoading,
	} = useCompareManagerState(compareOptions);

	const translations = useMemo(() => {
		return (data?.items[0].translations || {}) as ShareClassTranslationMap;
	}, [data]);

	const theme = useTheme();
	const mobileTheme = useMemo(() => {
		return {
			...theme,
			breakpoints: {
				base: "0em",
				sm: "1em",
				md: "2em",
				lg: "1000em",
				xl: "2000em",
				"2xl": "3000em"
			}
		};
	}, [theme]);

	const components = useMemo(() => {
		if (!componentList || !data) {
			return;
		}

		return getCompareComponents({ componentList, data });
	}, [componentList, data]);

	if (!externalIds.length) {
		return (
			<Flex>
				No externalIds found
			</Flex>
		)
	}

	return (
		<ChakraThemeProvider>
			<Flex>
				COMPARE PAGE
			</Flex>

			{isLoading && <Flex display="grid" placeItems="center">
				<Spinner color="brand.dark" size="xl" />
			</Flex>}

			{externalIds && <Flex flexDirection="column">
				<Flex>{location}</Flex>
				<Flex>{channel}</Flex>
				<Flex>{language}</Flex>
				{externalIds.map((id, index) => {
					return <Flex key={index}>{id}</Flex>;
				})}
			</Flex>}



			<ChakraProvider theme={mobileTheme}>
				{data?.items && (
					<BaseAccordion allowMultiple={true} variant="dividendInfoDrawer" px={5}>
						{components?.map(({ id, componentName, data: componentData, heading }, idx) => {
							const alignColumnContent = COMPARE_ALIGNED_COMPONENTS.indexOf(componentName) > -1;
							const CompareSection = getCompareSectionForComponent({ componentName, id });

							return (
								<CompareSection
									key={idx}
									heading={heading}
									data={data?.items}
									componentName={componentName}
									componentData={componentData as any}
									location={location}
									channel={channel}
									language={language}
									translations={translations}
								>
									<CompareSectionRow
										componentName={componentName}
										data={componentData as any}
										location={location}
										channel={channel}
										language={language}
										translations={translations}
										alignColumnContent={alignColumnContent}
									/>
								</CompareSection>
							);
						})}
					</BaseAccordion>
				)}
			</ChakraProvider>

		</ChakraThemeProvider>
	);
}